import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import Footer from '../Footer/index.js';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  setDoc,
  updateDoc,
  getDoc,
  orderBy,
  onSnapshot,
  serverTimestamp
} from 'firebase/firestore';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import { auth, database } from '../../firebaseConfig.js';
import { shuffle } from '../../utils/index.js';
import { isAdminUser } from '../../utils/admin';
import Offline from '../Offline/index.js';
import Login from './login.js';
import QuizForm from './Quizform.js';
import Sidebar from '../Sidebar/index.js';
import Header from '../Header/index.js';
import NotificationPanel from '../Notification Panel/index.js';
import { FiLogOut, FiUser, FiBell } from 'react-icons/fi';
import ProfileForm from '../Profile Form';
import { useTheme } from '../../context/ThemeContext';

import './Main.css';



const Main = ({ startQuiz }) => {
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [numOfQuestions, setNumOfQuestions] = useState(5);
  const [difficulty, setDifficulty] = useState('easy');
  const [questionsType, setQuestionsType] = useState('0');
  const [countdownTime, setCountdownTime] = useState({
    hours: 0,
    minutes: 120,
    seconds: 0,
  });
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [offline] = useState(false);
  const [user, setUser] = useState(null);
  const [attemptedQuestions, setAttemptedQuestions] = useState([]);
  const [mockTest, setMockTest] = useState('');
  const [mockTestOptions, setMockTestOptions] = useState([]);
  const [activeMockTest, setActiveMockTest] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isProfileIncomplete, setIsProfileIncomplete] = useState(false); // Add this state

  const secondsToTimeParts = (totalSeconds = 0) => {
    const safeSeconds = Math.max(0, Number(totalSeconds) || 0);
    const hours = Math.floor(safeSeconds / 3600);
    const minutes = Math.floor((safeSeconds % 3600) / 60);
    const seconds = safeSeconds % 60;
    return { hours, minutes, seconds };
  };

  const deriveTimeLimitSeconds = (data = {}) => {
    if (data.timeLimitSeconds) return data.timeLimitSeconds;
    if (data.timeLimitMinutes) return data.timeLimitMinutes * 60;
    if (data.durationSeconds) return data.durationSeconds;
    if (data.durationMinutes) return data.durationMinutes * 60;
    if (data.time_limit) return data.time_limit;
    return null;
  };

  const parseMockTestQuestions = (data = {}) => {
    if (Array.isArray(data.questions)) return data.questions;
    if (Array.isArray(data.mcqs)) return data.mcqs;
    if (Array.isArray(data.items)) return data.items;
    if (Array.isArray(data.data)) return data.data;
    if (Array.isArray(data.list)) return data.list;
    if (data.questions && typeof data.questions === 'object') {
      return Object.values(data.questions);
    }
    if (data.mcqs && typeof data.mcqs === 'object') {
      return Object.values(data.mcqs);
    }
    return [];
  };

  // Notification-related state
  const [notifications, setNotifications] = useState([
    {
      id: 'local-welcome',
      type: 'welcome',
      message: 'Welcome to the app!',
      timestamp: new Date().toISOString()
    },
    {
      id: 'local-results',
      type: 'info',
      message: 'Your quiz results are available.',
      timestamp: new Date().toISOString()
    },
  ]);
  const [showNotifications, setShowNotifications] = useState(false);

  // Sidebar toggle state
  const [showSidebar, setShowSidebar] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userName, setUserName] = useState('');

  useEffect(() => {
    const loadMockTests = async () => {
      try {
        const mockTestsRef = collection(database, 'Mock Tests');
        const snapshot = await getDocs(mockTestsRef);
        const options = snapshot.docs.map(docSnap => {
          const data = docSnap.data() || {};
          const timeLimitSeconds = deriveTimeLimitSeconds(data);
          const questionCount =
            data.defaultQuestionCount ||
            data.questionCount ||
            (Array.isArray(data.questions) ? data.questions.length : 0);

          return {
            id: docSnap.id,
            name: data.name || docSnap.id.replace(/_/g, ' '),
            description: data.description || data.summary || '',
            subjects: data.subjects || null,
            questionCount,
            timeLimitSeconds,
          };
        });
        setMockTestOptions(options);
      } catch (error) {
        console.error('Error loading mock tests:', error);
      }
    };

    loadMockTests();

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        await fetchAttemptedQuestions(currentUser.uid);

        // Check if the profile is incomplete and fetch user name
        const userRef = doc(database, 'users', currentUser.uid);
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setIsProfileIncomplete(!userData.profileCompleted);
          setIsAdmin(isAdminUser(currentUser, userData));

          // Fetch user name
          const name = userData?.personalInfo?.name || currentUser.displayName || currentUser.email?.split('@')[0] || 'User';
          setUserName(name);
        } else {
          // Fallback to display name or email
          const name = currentUser.displayName || currentUser.email?.split('@')[0] || 'User';
          setUserName(name);
        }
      } else {
        setIsProfileIncomplete(false);
        setIsAdmin(false);
        setUserName('');
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!mockTest) {
      setActiveMockTest(null);
      return;
    }

    const selected = mockTestOptions.find(option => option.id === mockTest);
    setActiveMockTest(selected || null);

    if (selected?.questionCount) {
      const normalizedCount = Math.min(Number(selected.questionCount) || 0, 300);
      if (normalizedCount) {
        setNumOfQuestions(normalizedCount);
      }
    }

    if (selected?.timeLimitSeconds) {
      setCountdownTime(secondsToTimeParts(selected.timeLimitSeconds));
    }
  }, [mockTest, mockTestOptions]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const notificationsRef = collection(database, 'notifications');
    const notificationsQuery = query(
      notificationsRef,
      orderBy('timestamp', 'desc')
    );

    const unsubscribe = onSnapshot(notificationsQuery, (snapshot) => {
      const newNotifications = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        type: doc.data().type || 'info'
      }));
      setNotifications(newNotifications);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const updateLastActive = async () => {
      if (!user) return;
      try {
        await updateDoc(doc(database, 'users', user.uid), {
          lastActiveAt: serverTimestamp()
        });
      } catch (err) {
        console.error('Failed to update last active timestamp:', err);
      }
    };

    updateLastActive();
  }, [user]);

  const fetchAttemptedQuestions = async (userId) => {
    try {
      const userRef = doc(database, 'users', userId);
      const userDoc = await getDoc(userRef);
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const attemptedQuestionIds = userData.attempted_questions || [];
        setAttemptedQuestions(attemptedQuestionIds);
      } else {
        await setDoc(userRef, {
          attempted_questions: [],
          role: 'user',
          createdAt: serverTimestamp(),
          lastActiveAt: serverTimestamp()
        }, { merge: true });
      }
    } catch (err) {
      console.error('Error fetching attempted questions:', err);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setUser(null);
    } catch (err) {
      console.error('Logout failed:', err);
    }
  };

  const handleTimeChange = (e) => {
    const { name, value } = e.target;
    setCountdownTime({ ...countdownTime, [name]: value });
  };

  const hasTimeConfigured =
    Number(countdownTime.hours) > 0 ||
    Number(countdownTime.minutes) > 0 ||
    Number(countdownTime.seconds) > 0;

  const hasSelectedCategories = selectedCategories.filter(category => category).length > 0;

  const allFieldsSelected = mockTest
    ? Number(numOfQuestions) > 0 && hasTimeConfigured
    : hasSelectedCategories &&
    Number(numOfQuestions) > 0 &&
    difficulty &&
    questionsType &&
    hasTimeConfigured;

  const fetchData = async () => {
    if (!user) {
      setError({ message: 'You must be logged in to start the quiz.' });
      return;
    }

    setProcessing(true);
    if (error) setError(null);

    try {
      let selectedQuestions = [];

      const requestedQuestionCount = Number(numOfQuestions) || 0;

      if (mockTest) {
        const mockTestRef = doc(database, 'Mock Tests', mockTest);
        const mockSnapshot = await getDoc(mockTestRef);
        if (!mockSnapshot.exists()) {
          setProcessing(false);
          setError({ message: 'Selected mock test could not be found.' });
          return;
        }

        const mockData = mockSnapshot.data();
        const mockQuestions = parseMockTestQuestions(mockData);

        if (!mockQuestions.length) {
          setProcessing(false);
          setError({ message: 'This mock test does not have any questions yet.' });
          return;
        }

        if (requestedQuestionCount > 0 && requestedQuestionCount > mockQuestions.length) {
          setProcessing(false);
          setError({
            message: `Only ${mockQuestions.length} questions are available in this mock test.`,
          });
          return;
        }

        const desiredCount =
          requestedQuestionCount > 0 ? requestedQuestionCount : mockQuestions.length;

        selectedQuestions = mockQuestions.slice(0, desiredCount).map((question, index) => {
          const correctAnswer =
            question.correct_answer ||
            question.correctAnswer ||
            question.answer ||
            question.correct;

          if (!correctAnswer) {
            throw new Error('One of the mock test questions is missing a correct answer.');
          }

          let incorrectAnswers = [];
          if (Array.isArray(question.incorrect_answers)) {
            incorrectAnswers = question.incorrect_answers;
          } else if (Array.isArray(question.incorrectAnswers)) {
            incorrectAnswers = question.incorrectAnswers;
          } else if (Array.isArray(question.incorrects)) {
            incorrectAnswers = question.incorrects;
          }

          if ((!incorrectAnswers || incorrectAnswers.length === 0) && Array.isArray(question.options) && correctAnswer) {
            incorrectAnswers = question.options.filter((option) => option !== correctAnswer);
          }

          const normalizedIncorrects = Array.from(
            new Set(
              (incorrectAnswers || [])
                .filter((option) => option && option !== correctAnswer)
                .map((option) => (typeof option === 'string' ? option.trim() : option))
            )
          );

          return {
            ...question,
            id: question.id || `${mockTest}-${index}`,
            category: question.category || mockData.category || 'Mock Test',
            difficulty: question.difficulty || 'medium',
            correct_answer: correctAnswer,
            incorrect_answers: normalizedIncorrects,
            solution: question.solution || question.explanation || '',
          };
        });
      } else {
        const questionsRef = collection(database, 'questions');
        const effectiveCategories = selectedCategories
          .filter(Boolean)
          .filter(
            (category) =>
              typeof category === 'string' &&
              category.toLowerCase() !== 'any category'
          );

        const includesAnyCategory = selectedCategories.some(
          (category) => typeof category === 'string' && category.toLowerCase() === 'any category'
        );

        if (includesAnyCategory) {
          const filters = [];
          if (difficulty !== 'any') filters.push(where('difficulty', '==', difficulty));
          if (questionsType !== '0') filters.push(where('type', '==', questionsType));

          const q = query(questionsRef, ...filters);
          const querySnapshot = await getDocs(q);
          const allQuestions = querySnapshot.docs.map((docSnapshot) => ({
            id: docSnapshot.id,
            ...docSnapshot.data(),
          }));

          const unattemptedQuestions = allQuestions.filter(
            (question) => !attemptedQuestions.includes(question.id)
          );

          selectedQuestions = shuffle(unattemptedQuestions).slice(0, requestedQuestionCount);
        } else {
          if (effectiveCategories.length === 0) {
            setProcessing(false);
            setError({ message: 'Please select at least one category.' });
            return;
          }

          const questionsPerCategory = Math.floor(
            requestedQuestionCount / effectiveCategories.length
          );

          for (const category of effectiveCategories) {
            const filters = [where('category', '==', category)];

            if (difficulty !== 'any') filters.push(where('difficulty', '==', difficulty));
            if (questionsType !== '0') filters.push(where('type', '==', questionsType));

            const q = query(questionsRef, ...filters);
            const querySnapshot = await getDocs(q);
            const categoryQuestions = querySnapshot.docs.map((docSnapshot) => ({
              id: docSnapshot.id,
              ...docSnapshot.data(),
            }));

            const unattemptedQuestions = categoryQuestions.filter(
              (question) => !attemptedQuestions.includes(question.id)
            );

            const selectedCategoryQuestions = shuffle(unattemptedQuestions).slice(
              0,
              questionsPerCategory
            );
            selectedQuestions = [...selectedQuestions, ...selectedCategoryQuestions];
          }

          const remainingQuestions = requestedQuestionCount - selectedQuestions.length;
          if (remainingQuestions > 0) {
            const lastCategory = effectiveCategories[effectiveCategories.length - 1];
            const additionalFilters = [where('category', '==', lastCategory)];

            if (difficulty !== 'any') additionalFilters.push(where('difficulty', '==', difficulty));
            if (questionsType !== '0') additionalFilters.push(where('type', '==', questionsType));

            const additionalQuery = query(questionsRef, ...additionalFilters);
            const additionalSnapshot = await getDocs(additionalQuery);
            const additionalQuestions = additionalSnapshot.docs
              .map((docSnapshot) => ({
                id: docSnapshot.id,
                ...docSnapshot.data(),
              }))
              .filter(
                (question) =>
                  !attemptedQuestions.includes(question.id) &&
                  !selectedQuestions.some((existing) => existing.id === question.id)
              );

            const additionalSelectedQuestions = shuffle(additionalQuestions).slice(
              0,
              remainingQuestions
            );
            selectedQuestions = [...selectedQuestions, ...additionalSelectedQuestions];
          }
        }
      }

      const expectedQuestionTotal =
        mockTest && requestedQuestionCount <= 0
          ? selectedQuestions.length
          : requestedQuestionCount;

      if (!selectedQuestions.length || (expectedQuestionTotal > 0 && selectedQuestions.length < expectedQuestionTotal)) {
        setProcessing(false);
        setError({
          message:
            'Not enough unattempted questions available. Please adjust your selection or try different categories.',
        });
        return;
      }

      const enhancedQuestions = selectedQuestions.map((element) => {
        const incorrectAnswers = Array.isArray(element.incorrect_answers)
          ? element.incorrect_answers
          : [];

        return {
          ...element,
          options: shuffle([element.correct_answer, ...incorrectAnswers]),
          solution: element.solution,
        };
      });

      setProcessing(false);

      const totalSeconds =
        Number(countdownTime.hours || 0) * 3600 +
        Number(countdownTime.minutes || 0) * 60 +
        Number(countdownTime.seconds || 0);

      if (totalSeconds <= 0) {
        setProcessing(false);
        setError({ message: 'Please set a valid time limit for your test.' });
        return;
      }

      // Determine test name for saving results
      const testName = mockTest
        ? mockTestOptions.find(m => m.value === mockTest)?.text || mockTest
        : selectedCategories.filter(c => c).join(', ') || 'Custom Quiz';

      startQuiz(enhancedQuestions, totalSeconds, {
        testType: mockTest ? 'mock' : 'custom',
        testName: testName,
        category: selectedCategories.filter(c => c).join(', ')
      });

      const newAttemptedIds = selectedQuestions.map((q) => q.id);
      const updatedAttemptedQuestions = Array.from(
        new Set([...attemptedQuestions, ...newAttemptedIds])
      );

      await updateDoc(doc(database, 'users', user.uid), {
        attempted_questions: updatedAttemptedQuestions,
      });

      setAttemptedQuestions(updatedAttemptedQuestions);
    } catch (err) {
      console.error('Error fetching questions:', err);
      setProcessing(false);
      setError({ message: 'Error fetching questions. Please try again later.' });
    }
  };
  const { isDark } = useTheme();

  // Memoize Sidebar to prevent re-renders (must be before early returns)
  const sidebarComponent = useMemo(() => {
    return showSidebar ? null : <Sidebar isAdmin={isAdmin} />;
  }, [showSidebar, isAdmin]);

  if (offline) return <Offline />;

  if (!user) {
    return (
      <div className="">
        <Login
          onLoginSuccess={(currentUser) => {
            setUser(currentUser);
          }}
          setError={setError}
          isProfileIncomplete={isProfileIncomplete}
          setIsProfileIncomplete={setIsProfileIncomplete}
        />
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? 'bg-dark-primary' : 'bg-gray-50'
      }`}>
      <div className="flex flex-col md:flex-row min-h-screen">
        {sidebarComponent}

        <div className="flex-1 flex flex-col">
          {/* Header */}
          <Header
            userName={userName}
            onNotificationClick={() => setShowNotifications(!showNotifications)}
            notificationCount={notifications.length}
          />

          <div className="flex-1 p-4 md:p-6 relative">
            {/* Notification Panel - Top Right Dropdown */}
            <NotificationPanel
              notifications={notifications}
              onClose={() => setShowNotifications(false)}
              isOpen={showNotifications}
            />

            {/* Profile Form */}
            {isProfileIncomplete && <ProfileForm user={user} setIsProfileIncomplete={setIsProfileIncomplete} />}

            <div className="max-w-4xl mx-auto">
              <div className={`flex items-center justify-between mb-6 ${isDark ? 'text-gray-200' : 'text-gray-700'
                }`}>
                <div className="text-lg flex items-center">
                  <FiUser className="mr-2" />
                  Welcome, {userName || user.email?.split('@')[0] || 'User'} <span role="img" aria-label="wave">👋</span>
                  <span className={`ml-4 text-lg font-semibold md:block hidden ${isDark ? 'text-purple-400' : 'text-purple-600'
                    }`}>
                    Prepare Ni, Prepify Kro! 🎉
                  </span>
                </div>
                {error && <div className="text-red-500 font-medium">{error.message}</div>}
              </div>

              <QuizForm
                mockTest={mockTest}
                setMockTest={setMockTest}
                selectedCategories={selectedCategories}
                setSelectedCategories={setSelectedCategories}
                numOfQuestions={numOfQuestions}
                setNumOfQuestions={setNumOfQuestions}
                difficulty={difficulty}
                setDifficulty={setDifficulty}
                questionsType={questionsType}
                setQuestionsType={setQuestionsType}
                countdownTime={countdownTime}
                handleTimeChange={handleTimeChange}
                fetchData={fetchData}
                processing={processing}
                allFieldsSelected={allFieldsSelected}
                mockTestOptions={mockTestOptions}
                activeMockTest={activeMockTest}
                isDark={isDark}
              />

              {/* Logout Button */}
              <div className="flex justify-center mt-8 mb-6">
                <button
                  onClick={handleLogout}
                  className={`px-6 py-3 rounded-xl flex items-center gap-2 font-medium transition-all ${isDark
                    ? 'bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20'
                    : 'bg-red-600 text-white hover:bg-red-700 shadow-lg'
                    }`}
                >
                  <FiLogOut className="text-lg" /> Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

Main.propTypes = {
  startQuiz: PropTypes.func.isRequired,
  autoStartConfig: PropTypes.shape({
    categories: PropTypes.arrayOf(PropTypes.string),
    mockTestId: PropTypes.string,
    questionCount: PropTypes.number,
    difficulty: PropTypes.string,
    questionsType: PropTypes.string,
    timeLimitSeconds: PropTypes.number,
  }),
};

Main.defaultProps = {
  autoStartConfig: null,
};

export default Main;